import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Link as LinkIcon, Share2, Edit, Phone, MapPin, Building, X, CheckCircle, Heart, User, Clock } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import '../styles/phoneInput.css';
import { useRelations } from '../hooks/useRelations';
import { useNotifications } from '../hooks/useNotifications';
import ImageCropper from '../components/ImageCropper';
import { gradientText, gradientButton, gradientHover } from '../theme';
import { theme } from '../theme';
import type { Relation } from '../types/relation';
import type { Notification } from '../types/notification';
import { useProfile } from '../hooks/useProfile';
import { supabase } from '../lib/supabase';
import { useCachedData } from '../hooks/useCachedData';
import { useAppSettings } from '../hooks/useAppSettings';
import { useAuthContext } from '../contexts/AuthContext';
import { getPaymentUrl } from '../services/paymentGatewayService';
import { CheckIcon } from '@heroicons/react/24/outline';
import { countUserCrushes } from '../services/searchService';
import PaymentFlow from '../components/PaymentFlow';
import { useAdmin } from '../hooks/useAdmin';
import { useHelpResources } from '../hooks/useHelpResources';

// Composant pour afficher le statut avec un dégradé SVG
const StatusBadge = React.memo(({ status }: { status: string }) => {
  const { t } = useTranslation();
  return (
    <div className="inline-block">
      <svg width="100" height="28" viewBox="0 0 100 28" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="statusGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={theme.colors.primary.start} />
            <stop offset="100%" stopColor={theme.colors.primary.end} />
          </linearGradient>
        </defs>
        <rect width="98" height="26" x="1" y="1" rx="13" stroke="url(#statusGradient)" strokeWidth="2" fill="transparent" />
        <text x="50" y="18" fontFamily="Arial" fontSize="12" fontWeight="600" fill="white" textAnchor="middle">
          {status === 'couple' ? t('profile.relations.status.en_couple') : t(`profile.relations.status.${status}`)}
        </text>
      </svg>
    </div>
  );
});

const UploadIcon = React.memo(({ className = "" }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="url(#uploadGradient)"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <defs>
      <linearGradient id="uploadGradient" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor={theme.colors.primary.start} />
        <stop offset="100%" stopColor={theme.colors.primary.end} />
      </linearGradient>
    </defs>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
));

const BellIcon = React.memo(({ className = "" }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="url(#bellGradient)"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <defs>
      <linearGradient id="bellGradient" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor={theme.colors.primary.start} />
        <stop offset="100%" stopColor={theme.colors.primary.end} />
      </linearGradient>
    </defs>
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
));

const SaveAnimation = () => {
  return (
    <div className="absolute inset-0 bg-black/40 rounded-xl flex items-center justify-center overflow-hidden">
      <div className="w-10 h-10 relative">
        <div className="absolute inset-0 border-4 border-white/20 rounded-full"></div>
        <div className="absolute inset-0 border-4 border-t-white rounded-full animate-spin"></div>
      </div>
    </div>
  );
};

function Profile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { profile, uploadAvatar, updateProfile } = useProfile();
  const { user } = useAuthContext();
  const { isAdmin } = useAdmin();
  const { contactLink } = useHelpResources();
  const { createRelation, updateRelation, getUserRelations, deleteRelation } = useRelations();
  const { getUserNotifications, markAsRead, markAllAsRead, deleteNotification, deleteAllNotifications } = useNotifications();
  const { getRelationDeletionPrice, formatPrice } = useAppSettings();
  const [isWaitingForDonation, setIsWaitingForDonation] = useState(false);
  const [waitingTime, setWaitingTime] = useState<number>(120);
  const [loadingDots, setLoadingDots] = useState('');
  const [showThankYou, setShowThankYou] = useState(false);
  const [crushCount, setCrushCount] = useState(0);
  
  // État pour contrôler l'affichage du pop-up d'ajout de relation
  const [showAddRelationPopup, setShowAddRelationPopup] = useState(false);
  
  // État pour le popup d'information sur l'administrateur
  const [showAdminInfoPopup, setShowAdminInfoPopup] = useState(false);
  
  // Vérifier si les services de paiement sont activés lors du chargement du composant
  useEffect(() => {
    const checkPaymentServices = async () => {};
    
    checkPaymentServices();
  }, []);

  // Afficher le pop-up d'ajout de relation à chaque entrée sur la page
  useEffect(() => {
    // Afficher le pop-up après un court délai pour une meilleure expérience utilisateur
    const timer = setTimeout(() => {
      setShowAddRelationPopup(true);
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Mettre à jour l'URL lorsque showAddRelation change
  const [showAddRelation, setShowAddRelation] = useState(false);
  
  // Effet pour mettre à jour l'URL quand le modal est ouvert ou fermé
  useEffect(() => {
    if (showAddRelation) {
      navigate({
        pathname: location.pathname,
        search: '?show_modal=add_relation',
      }, { replace: true });
    } else {
      // Si le modal est fermé, retirer le paramètre de l'URL
      if (location.search.includes('show_modal=add_relation')) {
        navigate({
          pathname: location.pathname,
          search: '',
        }, { replace: true });
      }
    }
  }, [showAddRelation, navigate, location.pathname, location.search]);

  // Charger le nombre de crush
  useEffect(() => {
    const loadCrushCount = async () => {
      if (!user) return;
      try {
        const count = await countUserCrushes(user.id);
        setCrushCount(count);
      } catch (error) {
        console.error('Erreur lors du chargement du nombre de crush:', error);
      }
    };
    loadCrushCount();
    // Écoute l'événement personnalisé pour rafraîchir le compteur
    const handler = () => loadCrushCount();
    window.addEventListener('crushes-updated', handler);
    return () => {
      window.removeEventListener('crushes-updated', handler);
    };
  }, [user]);
  
  // Utiliser le hook useCachedData pour les relations
  const {
    data: relationsData,
    isLoading: relationsLoading,
    invalidateCache: invalidateRelationsCache
  } = useCachedData<Relation[]>(
    'user-relations',
    getUserRelations,
    2 * 60 * 1000, // 2 minutes de cache
    [profile?.id] // Recharger si l'ID du profil change
  );

  // Utiliser le hook useCachedData pour les notifications
  const {
    data: notificationsData,
    isLoading: notificationsLoading,
    invalidateCache: invalidateNotificationsCache
  } = useCachedData<Notification[]>(
    'user-notifications',
    getUserNotifications,
    1 * 60 * 1000, // 1 minute de cache (plus court pour les notifications)
    [profile?.id] // Recharger si l'ID du profil change
  );
  
  const [showNotifications, setShowNotifications] = useState(false);
  const [editingRelation, setEditingRelation] = useState<string | null>(null);
  const [relations, setRelations] = useState<Relation[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [copiedRelationId, setCopiedRelationId] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState({
    name: '',
    photo: '',
    customLink: '',
    customLinkUrl: '',
    email: '',
    description: ''
  });
  const [newRelation, setNewRelation] = useState<Partial<Relation>>({
    target_name: '',
    target_phones: [''],
    target_country: '',
    target_city: '',
    status: 'couple',
    comment: ''
  });
  const [previewUrl, setPreviewUrl] = useState('');
  const [showProfileCropper, setShowProfileCropper] = useState(false);
  const [originalProfileImageUrl, setOriginalProfileImageUrl] = useState('');
  const [showRelationCropper, setShowRelationCropper] = useState(false);
  const [originalRelationImageUrl, setOriginalRelationImageUrl] = useState('');
  const [showSharePreview, setShowSharePreview] = useState(false);
  const [selectedRelationForShare, setSelectedRelationForShare] = useState<Relation | null>(null);
  
  // État pour le pop-up de confirmation de suppression
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [relationToDelete, setRelationToDelete] = useState<{id: string, name: string, isCertified: boolean} | null>(null);
  
  // État pour le composant PaymentFlow
  const [showPaymentFlow, setShowPaymentFlow] = useState(false);
  
  // Ajouter un état pour suivre les commentaires étendus
  const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set());
  
  // Ajouter un état pour la modification de la description
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [tempDescription, setTempDescription] = useState('');
  
  // Mettre à jour les états locaux lorsque les données en cache changent
  useEffect(() => {
    if (relationsData) {
      setRelations(relationsData);
    }
  }, [relationsData]);

  useEffect(() => {
    if (notificationsData) {
      setNotifications(notificationsData);
    }
  }, [notificationsData]);

  // Indicateur de chargement global
  const isLoading = relationsLoading || notificationsLoading;

  useEffect(() => {
    if (profile) {
      setUserProfile({
        name: profile.full_name || profile.email?.split('@')[0] || t('profile.userProfile.defaultName'),
        photo: profile.avatar_url || '',
        customLink: profile.custom_link || '',
        customLinkUrl: profile.custom_link_url || '',
        email: profile.email || '',
        description: profile.description || ''
      });
    }
  }, [profile, t]);

  const unreadNotificationsCount = notifications.filter(n => !n.read).length;

  const handleAddPhone = () => {
    setNewRelation(prev => ({
      ...prev,
      target_phones: [...(prev.target_phones || []), '']
    }));
  };

  const handleRemovePhone = (index: number) => {
    setNewRelation(prev => ({
      ...prev,
      target_phones: (prev.target_phones || []).filter((_, i) => i !== index)
    }));
  };

  const handlePhoneChange = (index: number, value: string) => {
    setNewRelation(prev => ({
      ...prev,
      target_phones: (prev.target_phones || []).map((phone, i) => i === index ? value : phone)
    }));
  };

  const handleMarkNotificationAsRead = async (notificationId: string) => {
    const { error } = await markAsRead(notificationId);
    if (!error) {
      setNotifications(notifications.map(notification => 
        notification.id === notificationId 
          ? { ...notification, read: true }
          : notification
      ));
      
      // Invalider le cache des notifications après une modification
      invalidateNotificationsCache();
    }
  };

  const handleMarkAllNotificationsAsRead = async () => {
    const { error } = await markAllAsRead();
    if (!error) {
      setNotifications(notifications.map(notification => ({ ...notification, read: true })));
      
      // Invalider le cache des notifications après une modification
      invalidateNotificationsCache();
    }
  };

  const handleDeleteNotification = async (notificationId: string) => {
    const { error } = await deleteNotification(notificationId);
    if (!error) {
      setNotifications(notifications.filter(notification => notification.id !== notificationId));
      
      // Invalider le cache des notifications après une suppression
      invalidateNotificationsCache();
    }
  };

  const formatNotificationTime = (date: string) => {
    const now = new Date();
    const notificationDate = new Date(date);
    const diff = now.getTime() - notificationDate.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}${t('profile.notifications.timeFormat.days')}`;
    if (hours > 0) return `${hours}${t('profile.notifications.timeFormat.hours')}`;
    if (minutes > 0) return `${minutes}${t('profile.notifications.timeFormat.minutes')}`;
    return t('profile.notifications.timeFormat.now');
  };

  const { getRootProps: getRelationRootProps, getInputProps: getRelationInputProps } = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png']
    },
    maxFiles: 1,
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];
      const url = URL.createObjectURL(file);
      setOriginalRelationImageUrl(url);
      setShowRelationCropper(true);
    }
  });

  const handleProfilePhotoClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const url = URL.createObjectURL(file);
        setOriginalProfileImageUrl(url);
        setShowProfileCropper(true);
      }
    };
    input.click();
  };

  const handleProfileCropComplete = async (croppedImageUrl: string) => {
    try {
      // Convert base64 to blob
      const response = await fetch(croppedImageUrl);
      const blob = await response.blob();
      const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });

      const { publicUrl, error } = await uploadAvatar(file);
      
      if (error) throw error;
      if (publicUrl) {
        setUserProfile(prev => ({ ...prev, photo: publicUrl }));
      }
    } catch (error) {
      console.error('Error updating avatar:', error);
    } finally {
      setShowProfileCropper(false);
    }
  };

  const handleProfileCropCancel = () => {
    setShowProfileCropper(false);
    if (originalProfileImageUrl) {
      URL.revokeObjectURL(originalProfileImageUrl);
    }
  };

  const handleRelationCropComplete = async (croppedImageUrl: string) => {
    try {
      // Convertir l'URL de l'image en Blob
      const response = await fetch(croppedImageUrl);
      const blob = await response.blob();
      const file = new File([blob], 'relation-photo.jpg', { type: 'image/jpeg' });
      
      // Générer un ID temporaire pour la relation (sera remplacé par l'ID réel après création)
      const tempRelationId = 'temp-' + Date.now();
      
      // Définir le chemin du fichier avec le format recommandé
      const fileExt = 'jpg';
      const filePath = `${profile?.id}/${tempRelationId}/${Date.now()}.${fileExt}`;
      
      // Télécharger l'image dans Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('relations')
        .upload(filePath, file, { upsert: true });
      
      if (uploadError) throw uploadError;
      
      // Obtenir l'URL publique
      const { data: { publicUrl } } = supabase.storage
        .from('relations')
        .getPublicUrl(filePath);
      
      // Mettre à jour l'URL de prévisualisation avec l'URL publique
      setPreviewUrl(publicUrl);
    } catch (error) {
      console.error('Erreur lors du téléchargement de l\'image:', error);
      // Fallback au comportement précédent en cas d'erreur
      setPreviewUrl(croppedImageUrl);
    } finally {
      setShowRelationCropper(false);
    }
  };

  const handleRelationCropCancel = () => {
    setShowRelationCropper(false);
    setPreviewUrl('');
    if (originalRelationImageUrl) {
      URL.revokeObjectURL(originalRelationImageUrl);
    }
  };

  const [isSaving, setIsSaving] = useState(false);

  const handleAddRelation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRelation.target_name) return;
    
    setIsSaving(true);
    
    try {
      const relationData = {
        target_photo: previewUrl,
        target_name: newRelation.target_name,
        target_phones: newRelation.target_phones || [''],
        target_country: newRelation.target_country || '',
        target_city: newRelation.target_city || '',
        status: newRelation.status || 'couple',
        confirmed: newRelation.confirmed || false,
        rejected: false,
        creator_name: userProfile.name,
        creator_avatar: userProfile.photo,
        comment: newRelation.comment || undefined
      };

      if (editingRelation) {
        // Récupérer la relation existante pour comparer les modifications
        const existingRelation = relations.find(r => r.id === editingRelation);
        
        // Créer un objet avec uniquement les champs modifiés
        const updatedFields: Partial<Relation> = {};
        
        // Vérifier chaque champ pour voir s'il a été modifié
        if (relationData.target_name !== existingRelation?.target_name && relationData.target_name !== existingRelation?.name) {
          updatedFields.target_name = relationData.target_name;
        }
        
        if (relationData.target_photo !== existingRelation?.target_photo && relationData.target_photo !== existingRelation?.photo) {
          updatedFields.target_photo = relationData.target_photo;
        }
        
        // Comparer les tableaux de téléphones
        const existingPhones = existingRelation?.target_phones || existingRelation?.phones || [];
        if (JSON.stringify(relationData.target_phones) !== JSON.stringify(existingPhones)) {
          updatedFields.target_phones = relationData.target_phones;
        }
        
        if (relationData.target_country !== existingRelation?.target_country && relationData.target_country !== existingRelation?.country) {
          updatedFields.target_country = relationData.target_country;
        }
        
        if (relationData.target_city !== existingRelation?.target_city && relationData.target_city !== existingRelation?.city) {
          updatedFields.target_city = relationData.target_city;
        }
        
        if (relationData.status !== existingRelation?.status) {
          updatedFields.status = relationData.status;
        }

        if (relationData.comment !== existingRelation?.comment) {
          updatedFields.comment = relationData.comment;
        }
        
        // Si aucun champ n'a été modifié, ne pas faire de mise à jour
        if (Object.keys(updatedFields).length === 0) {
          setEditingRelation(null);
          setShowAddRelation(false);
          return;
        }
        
        // Mettre à jour la relation avec les champs modifiés
        const { data: updatedRelation, error } = await updateRelation(editingRelation, relationData);
        if (!error && updatedRelation) {
          // Mettre à jour l'état local avec la relation mise à jour
          setRelations(prevRelations => 
            prevRelations.map(r => 
              r.id === editingRelation ? updatedRelation : r
            )
          );
          
          // Invalider le cache après une mise à jour réussie
          await invalidateRelationsCache();
        }
        
        setEditingRelation(null);
      } else {
        const { data, error } = await createRelation(relationData);
        if (!error && data) {
          if (previewUrl.includes('temp-')) {
            try {
              const urlParts = previewUrl.split('/');
              const fileName = urlParts[urlParts.length - 1];
              
              const tempPath = previewUrl.split('relations/')[1];
              const fileExt = fileName.split('.').pop();
              const newPath = `${profile?.id}/${data.id}/${Date.now()}.${fileExt}`;
              
              const { error: copyError } = await supabase.storage
                .from('relations')
                .copy(tempPath, newPath);
                
              if (!copyError) {
                const { data: { publicUrl } } = supabase.storage
                  .from('relations')
                  .getPublicUrl(newPath);
                  
                await updateRelation(data.id, { target_photo: publicUrl });
                
                data.target_photo = publicUrl;
                data.photo = publicUrl;
                
                await supabase.storage
                  .from('relations')
                  .remove([tempPath]);
              }
            } catch (moveError) {
              console.error('Erreur lors du déplacement de l\'image:', moveError);
            }
          }
          
          // Invalider d'abord le cache
          await invalidateRelationsCache();
          
          // Recharger les relations
          const { data: updatedRelations } = await getUserRelations();
          if (updatedRelations) {
            setRelations(updatedRelations);
          }
        }
      }

      setNewRelation({
        target_name: '',
        target_phones: [''],
        target_country: '',
        target_city: '',
        status: 'couple',
        comment: '',
        confirmed: false
      });
      setPreviewUrl('');
      setShowAddRelation(false);
    } finally {
      setIsSaving(false);
    }
  };

  // Supprimer les useCallback pour les gestionnaires d'événements
  const handleEditRelation = (relation: Relation) => {
    // Vérifier si la relation est certifiée pour définir correctement les champs éditables
    const isConfirmed = relation.confirmed;
    
    setNewRelation({
      target_name: relation.target_name || relation.name || '',
      target_phones: relation.target_phones || relation.phones || [''],
      target_country: relation.target_country || relation.country || '',
      target_city: relation.target_city || relation.city || '',
      status: relation.status,
      comment: relation.comment || '',
      confirmed: isConfirmed // Stocker le statut de certification
    });
    setPreviewUrl(relation.target_photo || relation.photo || '');
    setEditingRelation(relation.id);
    setShowAddRelation(true);
  };

  const handleDeleteRelation = async (relationId: string, isCertified: boolean) => {
    // Au lieu d'utiliser window.confirm, on affiche notre pop-up personnalisé
    const relationToDelete = relations.find(r => r.id === relationId);
    if (relationToDelete) {
      setRelationToDelete({
        id: relationId,
        name: relationToDelete.target_name || relationToDelete.name || '',
        isCertified
      });
      setShowDeleteConfirmation(true);
    }
  };

  // Récupérer le prix de suppression d'une relation
  const deletionPrice = getRelationDeletionPrice();
  const formattedDeletionPrice = formatPrice(deletionPrice);

  // Effet pour le timer d'attente
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isWaitingForDonation && waitingTime > 0) {
      timer = setInterval(() => {
        setWaitingTime(prev => {
          if (prev <= 1) {
            setIsWaitingForDonation(false);
            setShowThankYou(true);
            // Afficher le message de remerciement pendant 2 secondes
            setTimeout(() => {
              setShowThankYou(false);
              proceedWithDelete();
            }, 2000);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isWaitingForDonation, waitingTime]);

  // Effet pour l'animation des points
  useEffect(() => {
    let dotsTimer: NodeJS.Timeout;
    if (isWaitingForDonation) {
      dotsTimer = setInterval(() => {
        setLoadingDots(dots => dots.length >= 3 ? '' : dots + '.');
      }, 500);
    }
    return () => {
      if (dotsTimer) clearInterval(dotsTimer);
    };
  }, [isWaitingForDonation]);

  const proceedWithDelete = async () => {
    try {
      if (relationToDelete?.id) {
        // Attendre 2 secondes pour le message de remerciement
        setShowThankYou(true);
        await new Promise(resolve => setTimeout(resolve, 2000));
        setShowThankYou(false);

        // Supprimer immédiatement la relation de l'état local avant même d'appeler l'API
        setRelations(prev => prev.filter(r => r.id !== relationToDelete.id));
        
        // Récupérer la relation avant de la supprimer pour avoir l'ID du miroir si nécessaire
        const relationToRemove = relations.find(r => r.id === relationToDelete.id);
        const mirrorRelationId = relationToRemove?.mirror_relation_id;
        
        // Si c'est une relation miroir, supprimer également la version miroir de l'état local
        if (mirrorRelationId) {
          setRelations(prev => prev.filter(r => r.id !== mirrorRelationId));
        }
        
        // Appeler l'API pour supprimer la relation
        const { error } = await deleteRelation(relationToDelete.id);
        
        if (error) {
          console.error('Erreur lors de la suppression de la relation:', error);
          window.alert(t('profile.relations.deleteFailed'));
          // Même en cas d'erreur, ne pas recharger la relation supprimée localement
          return;
        }
        
        // Invalider le cache
        await invalidateRelationsCache();
        
        // Vider complètement le cache local
        localStorage.removeItem('user-relations');
        sessionStorage.removeItem('user-relations');
        
        // Rafraîchir les données sans recharger la page
        await invalidateRelationsCache();
        
        // Mettre à jour le compteur de notifications si nécessaire
        await invalidateNotificationsCache();
      }
    } catch (error) {
      console.error('Erreur lors de la suppression de la relation:', error);
      window.alert(t('profile.relations.deleteImpossible'));
    } finally {
      setRelationToDelete(null);
      setShowDeleteConfirmation(false);
    }
  };

  const confirmDelete = async () => {
    if (deletionPrice.amount > 0) {
      // Fermer la fenêtre de confirmation et montrer PaymentFlow à la place
      setShowDeleteConfirmation(false);
      setShowPaymentFlow(true);
      return;
    }
    
    // Si aucun prix n'est configuré, fermer immédiatement la fenêtre de confirmation
    setShowDeleteConfirmation(false);
    
    // Lancer la suppression en arrière-plan avec un ID temporaire
    const relationId = relationToDelete?.id;
    if (relationId) {
      // Réinitialiser la relation à supprimer immédiatement
      setRelationToDelete(null);
      
      // Lancer proceedWithDelete en arrière-plan
      setTimeout(() => {
        if (relationId) {
          // Restaurer temporairement l'ID pour la fonction proceedWithDelete
          setRelationToDelete({ 
            id: relationId, 
            name: relationToDelete?.name || '', 
            isCertified: relationToDelete?.isCertified || false 
          });
          proceedWithDelete();
        }
      }, 100);
    }
  };
  
  const cancelDelete = () => {
    // Fermer le modal de confirmation
    setShowDeleteConfirmation(false);
    // Réinitialiser la relation à supprimer
    setRelationToDelete(null);
  };

  const handleShareRelation = (relationId: string) => {
    // Au lieu de copier directement le lien, on affiche d'abord la prévisualisation
    const relationToShare = relations.find(r => r.id === relationId);
    if (relationToShare) {
      setSelectedRelationForShare(relationToShare);
      setShowSharePreview(true);
    }
  };

  const handleCopyShareLink = () => {
    if (!selectedRelationForShare) return;
    
    // Utiliser l'URL complète avec le domaine et le format de HashRouter (#/)
    const shareUrl = `${window.location.origin}/#/share/${selectedRelationForShare.id}`;
    navigator.clipboard.writeText(shareUrl)
      .then(() => {
        setCopiedRelationId(selectedRelationForShare.id);
        setTimeout(() => setCopiedRelationId(null), 2000);
        // Fermer la prévisualisation après la copie
        setTimeout(() => setShowSharePreview(false), 1000);
      })
      .catch(err => {
        console.error('Erreur lors de la copie du lien:', err);
      });
  };

  // Ajouter une fonction pour gérer l'expansion des commentaires
  const toggleCommentExpansion = (relationId: string) => {
    setExpandedComments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(relationId)) {
        newSet.delete(relationId);
      } else {
        newSet.add(relationId);
      }
      return newSet;
    });
  };

  // Remplacer le composant RelationCard par une fonction directe
  const renderRelationCard = (relation: Relation) => {
    const isCertified = relation.confirmed;
    
    return (
      <div key={relation.id} className="rounded-3xl overflow-hidden bg-darkGold relative">
        <div className="md:flex">
          {/* Image de la relation avec les boutons */}
          <div className="relative w-full md:w-[400px]" style={{ height: '300px' }}>
            <img
              src={relation.target_photo || 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400'}
              alt={relation.target_name || t('profile.relations.noName')}
              className="w-full h-full object-cover"
            />
            {/* Boutons d'édition et de suppression */}
            <div className="absolute top-4 right-4 z-10 flex gap-2">
              {relation.creator_id === user?.id && !relation.is_mirror && (
                <button
                  onClick={() => handleEditRelation(relation)}
                  className="rounded-full bg-amber-500 hover:bg-amber-600 w-8 h-8 flex items-center justify-center transition-colors"
                >
                  <Edit className="w-4 h-4 text-white" />
                </button>
              )}
              <button
                onClick={() => handleDeleteRelation(relation.id, isCertified)}
                className="rounded-full bg-red-500 hover:bg-red-600 w-8 h-8 flex items-center justify-center transition-colors"
              >
                <Trash2 className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>
          
          {/* Contenu de la carte */}
          <div className="p-6 md:flex-1 flex flex-col">
            <div>
              <div className="flex items-start justify-between">
                <h3 className="text-xl font-semibold">
                  {relation.target_name || t('profile.relations.noName')}
                </h3>
              </div>

              {relation.comment && (
                <p className="text-gray-400 italic text-sm mt-2">
                  <span className="text-gray-500">Note : </span>
                  <span className="hidden md:inline">{relation.comment}</span>
                  <span className="md:hidden">
                    {expandedComments.has(relation.id) || relation.comment.length <= 50 
                      ? relation.comment 
                      : `${relation.comment.slice(0, 50)}...`}
                    {relation.comment.length > 50 && (
                      <button
                        onClick={() => toggleCommentExpansion(relation.id)}
                        className={`ml-2 text-xs ${gradientText} hover:opacity-80 transition-opacity`}
                      >
                        {expandedComments.has(relation.id) ? t('common.seeLess') : t('common.seeMore')}
                      </button>
                    )}
                  </span>
                </p>
              )}

              <div className="mt-2 flex items-center justify-between">
                <StatusBadge status={relation.status} />
                {relation.created_at && (
                  <span className="text-xs text-gray-400">
                    {new Date(relation.created_at).toLocaleDateString()}
                  </span>
                )}
              </div>
              
              {/* Téléphone et Localisation sur la même ligne */}
              <div className="flex flex-col gap-2 text-gray-400 mt-2">
                {!relation.is_mirror && relation.target_phones && relation.target_phones.length > 0 && relation.target_phones.map((phone, index) => (
                  <div key={index}>
                    {index === 0 ? (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Phone className="w-4 h-4 mr-1" />
                          <span className="text-sm">{phone}</span>
                        </div>
                        {(relation.target_city || relation.target_country) && (
                          <div className="flex items-center">
                            <MapPin className="w-4 h-4 mr-1" />
                            <span className="text-sm">
                              {relation.target_city && relation.target_country
                                ? `${relation.target_city}, ${relation.target_country}`
                                : relation.target_city || relation.target_country}
                            </span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <Phone className="w-4 h-4 mr-1" />
                        <span className="text-sm">{phone}</span>
                      </div>
                    )}
                  </div>
                ))}
                
                {relation.is_mirror && (relation.target_city || relation.target_country) && (
                  <div className="flex items-center">
                    <MapPin className="w-4 h-4 mr-1" />
                    <span className="text-sm">
                      {relation.target_city && relation.target_country
                        ? `${relation.target_city}, ${relation.target_country}`
                        : relation.target_city || relation.target_country}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Espace flexible pour pousser le contenu suivant vers le bas */}
            <div className="flex-grow"></div>
            
            {/* Information du créateur pour les relations miroir */}
            {relation.is_mirror && relation.creator_id && (
              <div className="mt-2 pt-2 border-t border-gray-800">
                <div className="flex items-center text-gray-400">
                  <User className="w-4 h-4 mr-1" />
                  <span className="text-sm">
                    {t('profile.relations.createdBy', { name: relation.creator_name || t('profile.relations.unknown') })}
                  </span>
                </div>
              </div>
            )}

            {/* Badge de statut de certification et bouton de partage */}
            <div className="mt-4 pt-2 border-t border-gray-800">
              <div className="flex justify-between items-center">
                {relation.confirmed ? (
                  <div className="flex items-center text-green-500">
                    <CheckCircle className="w-5 h-5 mr-1" />
                    <span className="text-sm font-medium">{t('profile.relations.confirmation.confirmed')}</span>
                    {relation.certifier_id === user?.id && relation.creator_id !== user?.id && (
                      <span className="ml-2 text-sm bg-blue-600 text-white px-2 py-0.5 rounded-full">
                        {t('profile.relations.mirror')}
                      </span>
                    )}
                  </div>
                ) : relation.rejected ? (
                  <div className="flex items-center text-red-500">
                    <X className="w-5 h-5 mr-1" />
                    <span className="text-sm font-medium">{t('profile.relations.confirmation.rejected')}</span>
                  </div>
                ) : (
                  <div className="flex items-center text-yellow-500">
                    <Clock className="w-5 h-5 mr-1" />
                    <span className="text-sm font-medium">{t('profile.relations.confirmation.pending')}</span>
                  </div>
                )}

                {/* Bouton de partage */}
                {!relation.is_mirror && !relation.confirmed && (
                  <button
                    onClick={() => handleShareRelation(relation.id)}
                    className="bg-gradient-to-r from-primary-start to-primary-end text-white py-1 px-4 rounded-full text-sm flex items-center"
                  >
                    <Share2 className="w-4 h-4 mr-1" />
                    {t('profile.relations.share')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Afficher un indicateur de chargement si les données sont en cours de chargement
  if (isLoading && relations.length === 0 && notifications.length === 0) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center gap-4">
        <div className="relative w-12 h-12">
          <div className="absolute inset-0 rounded-full border-4 border-primary-end/20"></div>
          <div className="absolute inset-0 rounded-full border-4 border-t-primary-end animate-spin"></div>
        </div>
      </div>
    );
  }

  // Fermer le pop-up d'ajout de relation
  const handleCloseAddRelationPopup = () => {
    setShowAddRelationPopup(false);
  };

  // Gestionnaire de clic sur l'icône admin
  const handleAdminIconClick = () => {
    if (isAdmin) {
      navigate('/admin');
    } else {
      setShowAdminInfoPopup(true);
    }
  };

  // Ajouter une fonction pour gérer la modification de la description
  const handleEditDescription = () => {
    setTempDescription(userProfile.description);
    setIsEditingDescription(true);
  };

  // Ajouter une fonction pour sauvegarder la description
  const handleSaveDescription = async () => {
    try {
      await updateProfile({
        description: tempDescription
      });
      
      setUserProfile(prev => ({
        ...prev,
        description: tempDescription
      }));
      
      setIsEditingDescription(false);
    } catch (error) {
      console.error('Error updating description:', error);
    }
  };

  return (
    <div className="relative min-h-screen pb-20">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between py-4 w-full">
          {/* Répartition des icônes sur toute la largeur */}
          <div className="flex-1 flex justify-start">
            <div className="relative">
              <button
                onClick={() => navigate('/my-crush')}
                className="relative hover:opacity-80 transition-opacity p-2"
                title={t('common.crushes')}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="url(#heartGradient)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-6 h-6"
                >
                  <defs>
                    <linearGradient id="heartGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor={theme.colors.primary.start} />
                      <stop offset="100%" stopColor={theme.colors.primary.end} />
                    </linearGradient>
                  </defs>
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
                {crushCount > 0 && (
                  <span className="absolute -top-1 -right-1 text-white text-xs font-bold">
                    {crushCount > 9 ? '9+' : crushCount}
                  </span>
                )}
              </button>
            </div>
          </div>
          <div className="flex-1 flex justify-center">
            <div className="relative">
              <button
                onClick={handleAdminIconClick}
                className="relative hover:opacity-80 transition-opacity p-2"
                title={t('admin.header.admin')}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="url(#adminGradient)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-6 h-6"
                >
                  <defs>
                    <linearGradient id="adminGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor={theme.colors.primary.start} />
                      <stop offset="100%" stopColor={theme.colors.primary.end} />
                    </linearGradient>
                  </defs>
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </button>
            </div>
          </div>
          <div className="flex-1 flex justify-center">
            <div className="relative">
              <button
                onClick={() => navigate('/settings')}
                className="relative hover:opacity-80 transition-opacity p-2"
                title={t('common.settings')}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="url(#settingsGradient)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-6 h-6"
                >
                  <defs>
                    <linearGradient id="settingsGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor={theme.colors.primary.start} />
                      <stop offset="100%" stopColor={theme.colors.primary.end} />
                    </linearGradient>
                  </defs>
                  <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </button>
            </div>
          </div>
          <div className="flex-1 flex justify-end">
            <div className="relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative hover:opacity-80 transition-opacity p-2"
              >
                <BellIcon className="w-6 h-6" />
                {unreadNotificationsCount > 0 && (
                  <div className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center">
                    <span className="text-xs font-medium text-white">{unreadNotificationsCount}</span>
                  </div>
                )}
              </button>
              {showNotifications && (
                <div className="fixed sm:absolute inset-x-0 sm:inset-x-auto sm:right-0 top-0 sm:top-full sm:mt-2 h-screen sm:h-auto bg-black sm:bg-[#0A0A0A] sm:border sm:border-primary-start/20 sm:rounded-2xl sm:shadow-lg overflow-hidden z-50 sm:w-96">
                  <div className="sticky top-0 bg-[#0A0A0A] border-b border-primary-start/20 p-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-white font-semibold text-base sm:text-lg">{t('profile.notifications.title')}</h3>
                      <div className="flex items-center gap-4">
                        {unreadNotificationsCount > 0 ? (
                          <button
                            onClick={handleMarkAllNotificationsAsRead}
                            className={`text-sm ${gradientText} hover:opacity-80 transition-opacity`}
                          >
                            {t('profile.notifications.markAllAsRead')}
                          </button>
                        ) : notifications.length > 0 && (
                          <button
                            onClick={async () => {
                              const { error } = await deleteAllNotifications();
                              if (!error) {
                                setNotifications([]);
                                await invalidateNotificationsCache();
                              }
                            }}
                            className={`text-sm ${gradientText} hover:opacity-80 transition-opacity`}
                          >
                            {t('profile.notifications.deleteAll')}
                          </button>
                        )}
                        <button
                          onClick={() => setShowNotifications(false)}
                          className="sm:hidden text-gray-400 hover:text-white"
                        >
                          <X className="w-6 h-6" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="h-[calc(100vh-4rem)] sm:max-h-[480px] overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-8 text-center text-gray-400">
                        <div className="mb-4">
                          <BellIcon className="w-12 h-12 mx-auto opacity-50" />
                        </div>
                        {t('profile.notifications.empty')}
                      </div>
                    ) : (
                      notifications.map(notification => (
                        <div
                          key={notification.id}
                          className={`p-4 sm:p-5 border-b border-primary-start/20 ${gradientHover} transition-colors ${
                            !notification.read ? 'bg-gradient-to-r from-primary-start/5 to-primary-end/5' : ''
                          }`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm sm:text-base font-medium text-white">
                                {notification.translation_key 
                                  ? t(`notifications.${notification.translation_key}.title`, notification.translation_params || {})
                                  : notification.title}
                              </p>
                              <p className="text-sm text-gray-400 mt-1 sm:mt-2">
                                {notification.translation_key 
                                  ? t(`notifications.${notification.translation_key}.message`, notification.translation_params || {})
                                  : notification.message}
                              </p>
                              <p className="text-xs text-gray-500 mt-1 sm:mt-2">
                                {formatNotificationTime(notification.created_at)}
                              </p>
                            </div>
                            <div className="flex items-center gap-3">
                              {!notification.read && (
                                <button
                                  onClick={() => handleMarkNotificationAsRead(notification.id)}
                                  className={`${gradientText} hover:opacity-80 transition-opacity`}
                                >
                                  <div className="w-2.5 h-2.5 bg-gradient-to-r from-primary-start to-primary-end rounded-full" />
                                </button>
                              )}
                              <button
                                onClick={() => handleDeleteNotification(notification.id)}
                                className="text-gray-400 hover:text-red-500 transition-colors p-1"
                              >
                                <X className="w-4 h-4 sm:w-5 sm:h-5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center">
          <div className="relative group cursor-pointer" onClick={handleProfilePhotoClick}>
            <div className="w-24 h-24 relative">
              <div className="absolute inset-0 bg-gradient-to-r from-primary-start to-primary-end rounded-full p-[2px]">
                <div className="w-full h-full rounded-full overflow-hidden bg-black">
                  {userProfile.photo ? (
                    <img
                      src={userProfile.photo}
                      alt={userProfile.name}
                      className="w-full h-full object-cover rounded-full"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-[#0A0A0A]">
                      <User className="w-10 h-10 text-gray-600" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-4 relative w-full max-w-[300px]">
            <h1 className="text-xl font-bold text-center text-white">
              {!userProfile.name || userProfile.name === t('profile.userProfile.defaultName') || userProfile.name === profile?.email?.split('@')[0] ? t('profile.userProfile.defaultName') : userProfile.name}
            </h1>
          </div>
          
          {isEditingDescription ? (
            <div className="mt-2 max-w-[500px] mx-auto"> {/* Augmenter de 300px à 500px */}
              <div className="relative">
                <textarea
                  value={tempDescription}
                  onChange={(e) => {
                    // Limiter à 500 caractères
                    if (e.target.value.length <= 500) {
                      setTempDescription(e.target.value);
                    }
                  }}
                  className="w-full bg-transparent border-none rounded-lg px-3 py-2 text-white focus:outline-none focus:border-none resize-none min-h-[100px] text-sm"
                  maxLength={500}
                  placeholder={t('profile.userProfile.defaultDescription')}
                />
                <div className="flex justify-between mt-1">
                  <div className="text-xs text-gray-500">
                    {tempDescription.length}/500
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setIsEditingDescription(false)}
                      className="text-xs text-gray-400 hover:text-white transition-colors"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      onClick={handleSaveDescription}
                      className="text-xs text-primary-end hover:text-primary-start transition-colors"
                    >
                      {t('common.save')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div 
              className="mt-2 text-center text-gray-400 text-sm max-w-[500px] mx-auto cursor-pointer hover:text-white transition-colors px-2 py-1" /* Augmenter de 300px à 500px */
              onClick={handleEditDescription}
            >
              {userProfile.description || t('profile.userProfile.defaultDescription')}
            </div>
          )}
          
          <div className="relative w-full max-w-[300px] mt-0.5">
            <div className="flex items-center justify-center gap-2 -ml-5">
              <a 
                href={userProfile.customLinkUrl || '#'} 
                target="_blank" 
                rel="noopener noreferrer"
                className={`text-sm ${gradientText} hover:opacity-80 transition-opacity flex items-center gap-1`}
              >
                <LinkIcon className="w-4 h-4" />
                {userProfile.customLinkUrl ? userProfile.customLinkUrl.replace(/^https?:\/\//, '') : t('profile.userProfile.defaultLink')}
              </a>
            </div>
          </div>
        </div>

        <div className="mt-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl text-white">
              {t('profile.relations.title')} <span className="text-sm text-gray-400">({relations.length})</span>
            </h2>
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  setNewRelation({
                    target_name: '',
                    target_phones: [''],
                    target_country: '',
                    target_city: '',
                    status: 'couple',
                    comment: ''
                  });
                  setPreviewUrl('');
                  setEditingRelation(null);
                  setShowAddRelation(true);
                  setShowAddRelationPopup(false);
                }}
                className={`${gradientButton} text-white px-4 sm:px-6 py-2 rounded-xl flex items-center gap-2 ${showAddRelationPopup ? 'animate-pulse shadow-lg shadow-primary-end/30' : ''}`}
              >
                <Plus className="w-5 h-5" />
                <span className="hidden sm:inline">{t('profile.relations.add')}</span>
              </button>
              
              {/* Pop-up d'ajout de relation lié au bouton */}
              {showAddRelationPopup && (
                <div className="absolute right-0 top-14 z-50 w-64 animate-fadeIn">
                  <div className="bg-gradient-to-r from-primary-start to-primary-end p-px rounded-xl shadow-lg">
                    <div className="bg-[#0A0A0A] rounded-xl p-4 relative">
                      <button 
                        onClick={handleCloseAddRelationPopup}
                        className="absolute top-2 left-2 text-gray-400 hover:text-white"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      
                      <p className="text-white text-sm mb-3 pl-6">
                        {t('profile.addRelationPopup.message')}
                      </p>
                    </div>
                  </div>
                  <div className="w-4 h-4 bg-primary-end rotate-45 absolute -top-2 right-6 z-0"></div>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4">
            {relations.map(relation => renderRelationCard(relation))}
          </div>
        </div>
      </div>

      {showAddRelation && (
        <div className="fixed inset-0 bg-black/95 z-50 overflow-y-auto">
          <div className="max-w-lg mx-auto px-4 py-8 pb-24">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-2xl font-semibold text-white">
                {editingRelation ? t('profile.addRelation.editTitle') : t('profile.addRelation.title')}
              </h2>
              <button
                onClick={() => {
                  setShowAddRelation(false);
                  setEditingRelation(null);
                  setNewRelation({
                    target_name: '',
                    target_phones: [''],
                    target_country: '',
                    target_city: '',
                    status: 'couple',
                    comment: ''
                  });
                  setPreviewUrl('');
                }}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleAddRelation} className="space-y-6">
              <div 
                {...getRelationRootProps()} 
                className={`relative p-6 text-center cursor-pointer transition-all rounded-2xl ${
                  previewUrl ? 'bg-gradient-to-r from-primary-start/5 to-primary-end/5' : ''
                }`}
                style={{
                  background: previewUrl ? 'linear-gradient(to right, rgba(236, 72, 153, 0.05), rgba(184, 164, 78, 0.05))' : 'linear-gradient(black, black) padding-box, linear-gradient(to right, #ec4899, #B8A44E) border-box',
                  border: previewUrl ? 'none' : '2px dashed transparent',
                  borderRadius: '16px'
                }}
              >
                <input {...getRelationInputProps()} />
                {previewUrl ? (
                  <div className="space-y-2">
                    <div className="relative mx-auto w-fit">
                      <img 
                        src={previewUrl} 
                        alt="Preview" 
                        className="h-56 mx-auto rounded-xl object-cover"
                      />
                      {isSaving && <SaveAnimation />}
                    </div>
                    <p className="text-sm text-gray-500 mt-2">{t('profile.addRelation.form.photo.hint')}</p>
                  </div>
                ) : (
                  <div className="relative space-y-3 py-4">
                    <UploadIcon className="w-12 h-12 mx-auto" />
                    <p className="text-gray-400 font-medium">{t('profile.addRelation.form.photo.add')}</p>
                    <p className="text-sm text-gray-500">{t('profile.addRelation.form.photo.hint')}</p>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <input
                  type="text"
                  value={newRelation.target_name}
                  onChange={(e) => setNewRelation(prev => ({ ...prev, target_name: e.target.value }))}
                  className={`w-full bg-[#0A0A0A] border border-gray-800 rounded-xl px-4 py-3 focus:outline-none ${
                    editingRelation && relations.some(r => r.id === editingRelation && r.confirmed === true) 
                    ? 'opacity-70 cursor-not-allowed' 
                    : 'focus:border-primary-end'
                  } text-white`}
                  placeholder={t('profile.addRelation.form.name')}
                  maxLength={500}
                  disabled={!!editingRelation && relations.some(r => r.id === editingRelation && r.confirmed === true)}
                />

                <textarea
                  value={newRelation.comment || ''}
                  onChange={(e) => setNewRelation(prev => ({ ...prev, comment: e.target.value }))}
                  className="w-full bg-[#0A0A0A] border border-gray-800 rounded-xl px-4 py-3 focus:outline-none focus:border-primary-end text-white resize-none"
                  placeholder={t('profile.addRelation.form.comment')}
                  rows={3}
                  maxLength={500}
                />

                <div className="space-y-2">
                  {(newRelation.target_phones || []).map((phone, index) => (
                    <div key={index} className="relative flex gap-2">
                      <div className="relative flex-1">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" style={{ zIndex: 1 }}>
                          <Phone />
                        </div>
                        <PhoneInput
                          country={'cm'}
                          value={phone}
                          onChange={(value) => handlePhoneChange(index, value)}
                          containerClass="w-full"
                          inputClass="w-full bg-[#0A0A0A] border border-gray-800 rounded-xl pl-24 pr-4 py-3 focus:outline-none focus:border-primary-end text-white"
                          dropdownClass="!bg-[#0A0A0A] !text-white !border-gray-800 custom-dropdown"
                          searchClass="!bg-[#0A0A0A] !text-[#B8A44E] !border-gray-800 focus:!border-[#B8A44E]"
                          buttonStyle={{
                            background: 'transparent',
                            border: 'none',
                            position: 'absolute',
                            left: '2.75rem',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 2
                          }}
                          inputStyle={{
                            background: '#0A0A0A',
                            border: '1px solid rgb(31 41 55)',
                            borderRadius: '0.75rem',
                            color: 'white',
                            width: '100%',
                            height: '100%',
                            paddingLeft: '6rem'
                          }}
                          dropdownStyle={{
                            background: '#0A0A0A',
                            border: '1px solid rgb(31 41 55)',
                            color: 'white'
                          }}
                          specialLabel=""
                          enableSearch={true}
                          disableSearchIcon={true}
                          countryCodeEditable={false}
                        />
                      </div>
                      {(newRelation.target_phones || []).length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemovePhone(index)}
                          className="px-3 py-3 bg-red-500/10 text-red-500 rounded-xl hover:bg-red-500/20 transition-colors"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={handleAddPhone}
                    className={`w-full mt-2 px-4 py-3 rounded-xl border border-gray-800 text-gray-500 hover:bg-gradient-to-r hover:from-primary-start/5 hover:to-primary-end/5 transition-all flex items-center justify-center gap-2`}
                  >
                    <Plus className="w-4 h-4" />
                    {t('profile.addRelation.form.addPhone')}
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="relative">
                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type="text"
                      value={newRelation.target_country || ''}
                      onChange={(e) => setNewRelation(prev => ({ ...prev, target_country: e.target.value }))}
                      className="w-full bg-[#0A0A0A] border border-gray-800 rounded-xl pl-12 pr-4 py-3 focus:outline-none focus:border-primary-end text-white"
                      placeholder={t('profile.addRelation.form.country')}
                    />
                  </div>
                  <div className="relative">
                    <Building className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type="text"
                      value={newRelation.target_city}
                      onChange={(e) => setNewRelation(prev => ({ ...prev, target_city: e.target.value }))}
                      className="w-full bg-[#0A0A0A] border border-gray-800 rounded-xl pl-12 pr-4 py-3 focus:outline-none focus:border-primary-end text-white"
                      placeholder={t('profile.addRelation.form.city')}
                    />
                  </div>
                </div>

                <select
                  value={newRelation.status}
                  onChange={(e) => setNewRelation(prev => ({ ...prev, status: e.target.value as 'couple' | 'married' | 'ex' | 'date' }))}
                  className="w-full bg-[#0A0A0A] border border-gray-800 rounded-xl px-4 py-3 focus:outline-none focus:border-primary-end text-white"
                >
                  <option value="couple">{t('profile.relations.status.en_couple')}</option>
                  <option value="married">{t('profile.relations.status.married')}</option>
                  <option value="date">{t('profile.relations.status.date')}</option>
                  {editingRelation && (
                    <option value="ex">{t('profile.relations.status.ex')}</option>
                  )}
                </select>
              </div>

              <button
                type="submit"
                disabled={isSaving}
                className={`w-full ${gradientButton} text-white font-medium py-3 px-6 rounded-xl flex items-center justify-center gap-2`}
              >
                {isSaving ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {editingRelation ? t('profile.addRelation.form.updating') : t('profile.addRelation.form.saving')}
                  </>
                ) : (
                  <>
                    {editingRelation ? (
                    <>
                      <Edit className="w-5 h-5" />
                      {t('profile.addRelation.form.update')}
                    </>
                  ) : (
                    <>
                      <Plus className="w-5 h-5" />
                      {t('profile.addRelation.form.submit')}
                    </>
                  )}
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {showSharePreview && selectedRelationForShare && (
        <div className="fixed inset-0 bg-black/95 z-50 overflow-y-auto">
          <div className="max-w-lg mx-auto px-4 py-8">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-2xl font-semibold text-white">
                {t('profile.shareRelation.title')}
              </h2>
              <button
                onClick={() => setShowSharePreview(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="bg-[#0A0A0A] rounded-2xl overflow-hidden mb-6">
              <div className="p-6 text-center">
                <h1 className="text-2xl font-bold mb-4">
                  {userProfile.name} {t('share.title', { status: selectedRelationForShare.status === 'couple' ? t('profile.relations.status.en_couple') : t(`profile.relations.status.${selectedRelationForShare.status}`) })}
                </h1>
                <p className={`text-lg ${gradientText}`}>
                  {t('share.question')}
                </p>
              </div>

              <div className="relative flex justify-center items-center py-8">
                <div className="relative flex items-center justify-center mx-auto px-2">
                  {/* Photo du créateur */}
                  <div className="relative">
                    <div 
                      className="w-32 h-32 relative rounded-[30px] overflow-hidden transform -rotate-6"
                      style={{
                        clipPath: 'path("M 0 30 C 0 13.432 13.432 0 30 0 H 130 C 146.568 0 160 13.432 160 30 V 130 C 160 146.568 146.568 160 130 160 H 80 L 30 160 C 13.432 160 0 146.568 0 130 V 30 Z")',
                      }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-primary-start to-primary-end p-1">
                        <div className="absolute inset-0 bg-black">
                          <img
                            src={userProfile.photo}
                            alt={t('share.creatorAlt')}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Cœur */}
                  <div className="absolute z-10">
                    <Heart className="w-12 h-12 text-pink-500 fill-pink-500 drop-shadow-[0_0_10px_rgba(236,72,153,0.5)]" />
                  </div>

                  {/* Photo de la relation */}
                  <div className="relative">
                    <div 
                      className="w-32 h-32 relative rounded-[30px] overflow-hidden transform rotate-6"
                      style={{
                        clipPath: 'path("M 0 30 C 0 13.432 13.432 0 30 0 H 130 C 146.568 0 160 13.432 160 30 V 130 C 160 146.568 146.568 160 130 160 H 80 L 30 160 C 13.432 160 0 146.568 0 130 V 30 Z")',
                      }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-primary-start to-primary-end p-1">
                        <div className="absolute inset-0 bg-black">
                          <img
                            src={selectedRelationForShare.photo}
                            alt={t('share.targetAlt')}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 text-center">
                <p className="text-gray-400 text-xs mb-4">
                  {t('share.creatorMessage')}
                </p>
                <button
                  onClick={handleCopyShareLink}
                  className={`w-full ${gradientButton} text-white font-medium py-3 px-6 rounded-xl flex items-center justify-center gap-2`}
                >
                  <Share2 className="w-5 h-5" />
                  {copiedRelationId === selectedRelationForShare.id ? t('profile.relations.copied') : t('share.copyLink')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showProfileCropper && originalProfileImageUrl && (
        <ImageCropper
          imageUrl={originalProfileImageUrl}
          onCropComplete={handleProfileCropComplete}
          onCancel={handleProfileCropCancel}
          aspectRatio={1}
        />
      )}

      {showRelationCropper && originalRelationImageUrl && (
        <ImageCropper
          imageUrl={originalRelationImageUrl}
          onCropComplete={handleRelationCropComplete}
          onCancel={handleRelationCropCancel}
          aspectRatio={1}
        />
      )}

      {/* Message d'attente de don flottant (indépendant du modal de confirmation) */}
      {isWaitingForDonation && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-50">
          <div className="text-center relative pt-6">
            <button
              onClick={() => {
                setIsWaitingForDonation(false);
                setWaitingTime(120);
                setLoadingDots('');
                setRelationToDelete(null);
                setShowDeleteConfirmation(false);
              }}
              className="absolute -top-4 -right-4 w-8 h-8 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors flex items-center justify-center"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
            {/* Bouton pour finaliser le paiement */}
            <button
              onClick={() => {
                if (relationToDelete?.id) {
                  getPaymentUrl(
                    relationToDelete.id,
                    deletionPrice.amount,
                    `Suppression de la relation avec ${relationToDelete.name || 'inconnu'}`
                  ).then(url => {
                    if (url?.url && typeof url.url === 'string') {
                      window.open(url.url, '_blank');
                    }
                  });
                }
              }}
              className="px-6 py-2 rounded-xl bg-gradient-to-r from-primary-start to-primary-end text-white text-sm font-medium hover:opacity-90 transition-opacity animate-pulse-payment"
            >
              {t('profile.deleteRelation.continueDonation')} ({formattedDeletionPrice})
            </button>
            <p className="text-yellow-500 font-medium text-sm mt-4 whitespace-pre-line opacity-80">
              {t('profile.deleteRelation.waitingDonation')}{loadingDots}
            </p>
          </div>
        </div>
      )}

      {/* Message de remerciement flottant */}
      {showThankYou && (
        <div className="flex items-center justify-center space-x-2">
          <div className="validation-icon">
            <CheckIcon className="h-6 w-6" />
          </div>
        </div>
      )}

      {/* Pop-up de confirmation de suppression */}
      {showDeleteConfirmation && relationToDelete && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#121212] rounded-xl max-w-md w-full p-5 animate-fade-in-up">
            <div className="flex flex-col items-center mb-3">
              <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mb-3">
                <Trash2 className="w-6 h-6 text-red-500" />
              </div>
              <h3 className="text-lg font-semibold text-white text-center">
                {t('profile.deleteRelation.title')}
              </h3>
              <p className="text-white text-center text-sm mt-2">
                {t('profile.deleteRelation.confirmation', { name: relationToDelete.name })}
              </p>
              {deletionPrice.amount > 0 && (
                <div className="mt-3 p-2 bg-yellow-500/10 rounded-lg w-full">
                  <p className="text-yellow-400 text-xs text-center">
                    <span className="font-semibold">{t('profile.deleteRelation.cost', { price: formattedDeletionPrice })}</span>
                  </p>
                </div>
              )}
            </div>
            <div className="flex gap-2 mt-3">
              <button
                onClick={cancelDelete}
                className="flex-1 py-2 px-3 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-colors text-sm"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 py-2 px-3 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors flex items-center justify-center gap-1 text-sm"
              >
                <Trash2 className="w-3 h-3" />
                {t('profile.deleteRelation.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PaymentFlow component */}
      {showPaymentFlow && relationToDelete && (
        <PaymentFlow
          amount={deletionPrice.amount}
          currency={deletionPrice.currency}
          service="RELATION_DELETION"
          userName={userProfile.name}
          onSuccess={() => {
            setShowPaymentFlow(false);
            proceedWithDelete();
          }}
          onCancel={() => {
            setShowPaymentFlow(false);
            setRelationToDelete(null);
          }}
        />
      )}

      {/* Pop-up d'information sur l'administrateur */}
      {showAdminInfoPopup && (
        <div className="fixed inset-0 bg-black/80 flex items-start justify-center z-50 p-4 animate-fadeIn mt-16">
          <div className="bg-gradient-to-r from-primary-start/10 to-primary-end/10 p-px rounded-xl max-w-md w-full">
            <div className="bg-black rounded-xl p-5 relative">
              <button 
                onClick={() => setShowAdminInfoPopup(false)}
                className="absolute top-2 right-2 text-gray-400 hover:text-white"
              >
                <X size={20} />
              </button>
              <p className="text-left text-white/90 text-sm font-medium mb-4">
                {t('admin.header.becomeAdmin')}
              </p>
              <div className="flex justify-start">
                <a 
                  href={contactLink || "#"}
                  target="_blank"
                  rel="noopener noreferrer" 
                  className={`${gradientButton} text-left px-4 py-2 rounded-lg text-white text-xs font-medium`}
                >
                  {t('admin.header.contactUs')}
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile